"use client";

import { c as _c } from "react/compiler-runtime";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef } from "react";
import { useTenant } from "@/contexts/tenant-provider";
import { useUser } from "@/contexts/user-provider";
import { useModule } from "@/hooks/use-module";
import { useWebSocketCallbackSubscription } from "@/hooks/use-websocket-subscription";
import { PERMISSIONS, hasPermission } from "@/lib/permissions";
import { AlertDetail } from "@/types/alert";
import { AlertType } from "@/types/enums/alert-type";
import { WaitlistCacheScope, activateWaitlistCacheScope, applyWaitlistUpdate, disposeWaitlistCacheScope, isWaitlistQuery, isWaitlistUpdateData } from "@/query/waitlist-cache";
import { recoverWaitlistCache } from "@/query/waitlist-recovery";

/** One console-wide callback subscription, including off-screen cached lists. */
export function useWaitlistRealtimeCache() {
  const $ = _c(20);
  const client = useQueryClient();
  const {
    tenant
  } = useTenant();
  const {
    currentUser
  } = useUser();
  const hasWaitlist = useModule("WAITLIST");
  let t0;
  if ($[0] !== currentUser || $[1] !== hasWaitlist) {
    t0 = hasWaitlist && hasPermission(currentUser, PERMISSIONS.SCHEDULING_WAITLIST_MANAGE);
    $[0] = currentUser;
    $[1] = hasWaitlist;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const allowed = t0;
  let t1;
  if ($[3] !== allowed || $[4] !== currentUser || $[5] !== tenant) {
    t1 = tenant && currentUser && allowed ? JSON.stringify([tenant.subDomain, currentUser.id, [...(currentUser.permissions ?? [])].sort(), [...(currentUser.visibleLocationIds ?? [])].sort(), currentUser.roles]) : null;
    $[3] = allowed;
    $[4] = currentUser;
    $[5] = tenant;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const identity = t1;
  const previousIdentity = useRef(null);
  const authorizationLost = useRef(false);
  const established = useRef(false);
  const wasConnected = useRef(null);
  const active = useRef(null);
  let t2;
  let t3;
  if ($[7] !== client || $[8] !== identity) {
    t2 = () => {
      if (!identity) {
        if (previousIdentity.current !== null) {
          authorizationLost.current = true;
        }
        return;
      }
      const needsRecovery = authorizationLost.current || previousIdentity.current !== null && previousIdentity.current !== identity;
      authorizationLost.current = false;
      previousIdentity.current = identity;
      const scope = activateWaitlistCacheScope(client, identity);
      active.current = {
        identity,
        scope,
        needsRecovery
      };
      return () => {
        active.current = null;
        disposeWaitlistCacheScope(client, scope);
      };
    };
    t3 = [client, identity];
    $[7] = client;
    $[8] = identity;
    $[9] = t2;
    $[10] = t3;
  } else {
    t2 = $[9];
    t3 = $[10];
  }
  useEffect(t2, t3);
  let t4;
  if ($[11] !== client || $[12] !== identity) {
    t4 = message => {
      const current = active.current;
      if (!current || current.identity !== identity) {
        return;
      }
      if (message.type === AlertType.WAITLIST_UPDATED && isWaitlistUpdateData(message.data)) {
        applyWaitlistUpdate(client, current.scope, message.data, message.createdDate);
      }
    };
    $[11] = client;
    $[12] = identity;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const handleMessage = t4;
  let t5;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = [AlertType.WAITLIST_UPDATED];
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const {
    isConnected
  } = useWebSocketCallbackSubscription(t5, handleMessage, identity !== null, "waitlistRealtimeCache");
  let t6;
  let t7;
  if ($[15] !== client || $[16] !== identity || $[17] !== isConnected) {
    t6 = () => {
      const current_0 = active.current;
      if (!isConnected) {
        if (established.current && current_0 && wasConnected.current !== false) {
          current_0.needsRecovery = true;
        }
        wasConnected.current = false;
        return;
      }
      const firstConnection = !established.current && wasConnected.current === false;
      wasConnected.current = true;
      established.current = true;
      if (!current_0 || current_0.identity !== identity) {
        return;
      }
      if (firstConnection && !current_0.needsRecovery) {
        const hasEarlierRead = client.getQueryCache().findAll({
          predicate: isWaitlistQuery
        }).some(_temp);
        current_0.needsRecovery = hasEarlierRead;
      }
      if (!current_0.needsRecovery) {
        return;
      }
      const recover = async () => {
        if (current_0.scope.recovery) {
          await current_0.scope.recovery.catch(_temp2);
        }
        if (active.current !== current_0 || !wasConnected.current || !current_0.needsRecovery) {
          return;
        }
        current_0.needsRecovery = false;
        await recoverWaitlistCache(client, current_0.scope);
      };
      recover().catch(_temp3);
    };
    t7 = [client, identity, isConnected];
    $[15] = client;
    $[16] = identity;
    $[17] = isConnected;
    $[18] = t6;
    $[19] = t7;
  } else {
    t6 = $[18];
    t7 = $[19];
  }
  useEffect(t6, t7);
}
function _temp3() {}
function _temp2() {}
function _temp(query) {
  return query.state.data !== undefined || query.state.fetchStatus === "fetching" || query.state.status === "error";
}